import { useState } from 'react';
import styled, { css } from 'styled-components';
import type { AddToBasketParams, BasketLineSubscriptionNudge } from 'autogen/swagger/all';
import { AddToBasket } from 'autogen/swagger/all';
import Checkbox from 'components/shared/inputs/checkbox';
import {
    BorderRadiusMedium,
    BorderWidthSmall,
    ColorBrandBlue1,
    ColorNordicGrey1,
    ColorNordicGrey4,
    Spacing8,
    Spacing12,
    Spacing24
} from 'autogen/design-tokens/tokens';
import { SvgSprite } from 'modules/svgSprite';
import { searchParamsToObject } from 'modules/helpers/urlParams';
import { breakpoint, sizes } from 'variables';

import { GeneralExceptionError } from 'autogen/translation-keys/trans-website-shared-error';
import track from 'modules/tracking/dataLayer';
import UAEventTracking from 'modules/tracking/UAEventTracking';
import useGA4Tracking from 'modules/tracking/GA4/GA4Tracking';
import { updateBasket } from 'components/basket/shared/basket';
import { Typography } from 'components/generic/typography';
import SubscriptionFrequencySelect from 'components/basket/BasketItem/components/Actions/components/SubscriptionFrequencySelect';
import Flex from 'components/Layout/components/Flex';
import { useProductStore } from 'components/pages/PDP/shared/productStore';
import toast from 'components/Toast';
import { useTranslation } from 'react-i18next';

const Container = styled(Flex.Column)<{ checked: boolean }>`
    padding: ${Spacing8};
    border: ${BorderWidthSmall} solid ${({ checked }) => (checked ? ColorBrandBlue1 : ColorNordicGrey1)};
    border-radius: ${BorderRadiusMedium};
`;

const Wrapper = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;

    ${breakpoint.up(sizes.md)} {
        grid-auto-flow: column;
    }
`;

const Label = styled.label`
    display: flex;
    margin: 0;

    ${breakpoint.up(sizes.md)} {
        grid-row: span 3;
    }
`;

const StyledCheckbox = styled(Checkbox)`
    display: block;
`;

const LabelText = styled.b`
    margin-left: ${Spacing8};
    cursor: pointer;

    ${breakpoint.up(sizes.md)} {
        grid-column: 2;
    }
`;

const Usp = styled.div`
    display: flex;
    align-items: baseline;
`;

const UspIcon = styled(SvgSprite)`
    width: 12px;
    height: 12px;
`;

const UspText = styled(Typography.Body)`
    margin-left: ${Spacing8};
`;

const Price = styled.div`
    display: flex;

    &:first-child {
        padding-bottom: ${Spacing8};
        border-bottom: ${BorderWidthSmall} solid ${ColorNordicGrey4};

        ${breakpoint.up(sizes.md)} {
            padding: 0;
            border: none;
        }
    }
`;

const PriceTitleStyle = css`
    flex: 1;
    margin-right: ${Spacing24};
`;

const PriceTitle = styled.b`
    ${PriceTitleStyle}
`;

const SubPriceTitle = styled.span`
    ${PriceTitleStyle}
`;

const FrequencyContainer = styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${ColorNordicGrey4};
    border-radius: ${BorderRadiusMedium};
    padding: ${Spacing12};
    margin-top: ${Spacing8};
    grid-column: 1/-1;

    ${breakpoint.up(sizes.md)} {
        flex-direction: row;
        align-items: center;
        grid-row: 1 /-1;
        grid-column: span 2 / 4;
        margin-top: 0;
    }
`;

const FrequencyTexts = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1;
    margin-bottom: ${Spacing8};

    ${breakpoint.up(sizes.md)} {
        margin: 0;
    }
`;

export interface ISubscriptionNudge {
    model: BasketLineSubscriptionNudge;
}

export default function SubscriptionNudge({ model }: ISubscriptionNudge): JSX.Element {
    const {
        isToggled,
        toggleActionLinkAction,
        aboutSubscriptionLinkAction,
        priceInfo: { isIntroPrice, isFixedLowPrice, showSubscriptionPrice, subTotal, subscriptionPrice },
        texts: {
            buyOnSubscription,
            noBinding,
            alwaysFreeShipping,
            introPrice,
            firstBuy,
            everyBuy,
            priceHereafter,
            chooseDeliveryFrequency,
            deliveryFrequencyNote
        },
        frequencyInfo: { choosable }
    } = model;

    const [subChecked, setSubChecked] = useState<boolean>(isToggled);
    const { setIsSubscription } = useProductStore();
    const { t } = useTranslation();
    const GA4Tracking = useGA4Tracking();

    async function toggleSubscription(checked: boolean) {
        setSubChecked(checked);
        setIsSubscription(checked);

        const url = new URL(toggleActionLinkAction.url, window.location.origin);
        const { searchParams } = url;

        const params = searchParamsToObject(searchParams, true) as AddToBasketParams;

        try {
            const response = await AddToBasket(params);
            if (!response.success) {
                throw Error(`Response not successful (${response.message})`);
            }
            if (response.dataLayerDto) {
                track(response.dataLayerDto);
            }

            GA4Tracking({
                eventName: 'toggle_subscription',
                category: 'ecommerce',
                eventParams: [
                    { param_name: 'checkout_step', param_value: 1 },
                    { param_name: 'toggle', param_value: checked }
                ]
            });

            UAEventTracking({
                eventCategory: 'basket',
                eventAction: 'toggleSubscription',
                eventLabel: checked ? 'toggleOn' : 'toggleOff'
            });

            updateBasket();
        } catch (error) {
            toast.error(t(GeneralExceptionError));
            console.error(error);
        }
    }

    function getTitle() {
        if (isIntroPrice) return introPrice;
        if (isFixedLowPrice) return everyBuy;
        return firstBuy;
    }

    return (
        <Container checked={subChecked} gap={Spacing8}>
            <Flex direction={{ xs: 'column', md: 'row' }} justifyContent={{ xs: 'center', md: 'space-between' }}>
                <Flex.Column gap={Spacing8}>
                    <Label>
                        <StyledCheckbox checked={subChecked} onChange={() => toggleSubscription(!subChecked)} />
                        <LabelText>{buyOnSubscription}</LabelText>
                    </Label>
                    <Flex.Row>
                        <Usp>
                            <UspIcon sprite="Checkmark" />
                            <UspText forwardedAs="span" size="M">
                                {noBinding}
                            </UspText>
                        </Usp>
                        <Usp>
                            <UspIcon sprite="Checkmark" />
                            <UspText forwardedAs="span" size="M">
                                {alwaysFreeShipping}
                            </UspText>
                        </Usp>
                    </Flex.Row>
                </Flex.Column>
                <Flex.Column gap={Spacing8}>
                    <Price>
                        <PriceTitle>{getTitle()}</PriceTitle>
                        <b>{subTotal}</b>
                    </Price>
                    {showSubscriptionPrice && subChecked && (
                        <Price>
                            <SubPriceTitle>{priceHereafter}</SubPriceTitle>
                            <span>{subscriptionPrice}</span>
                        </Price>
                    )}
                </Flex.Column>
            </Flex>

            {subChecked && !!choosable.length && (
                <Wrapper>
                    <FrequencyContainer>
                        <FrequencyTexts>
                            <b>{chooseDeliveryFrequency}</b>
                            <span>{deliveryFrequencyNote}</span>
                        </FrequencyTexts>
                        <SubscriptionFrequencySelect model={model.frequencyInfo} />
                    </FrequencyContainer>
                </Wrapper>
            )}
        </Container>
    );
}
